import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../../shared/components/Layout';
import UserDetailModal from '../../shared/components/UserDetailModal';
import { useAuth } from '../auth/AuthContext';
import { useUserNames, formatUserLabel } from '../../shared/hooks/useUserNames';
import { extractErrorMessage } from '../../shared/utils/errors';
import { formatCryptoAmount } from './format';
import {
  HoldingLockRecord,
  HoldingSummaryRow,
  getHoldingSettings,
  getHoldingsSummary,
  listUserHoldings,
  releaseUserHoldings,
  updateHoldingSettings,
} from './holdingApi';

type PeriodUnit = 'minutes' | 'hours' | 'days';

const UNIT_MINUTES: Record<PeriodUnit, number> = { minutes: 1, hours: 60, days: 1440 };

const MAX_PERIOD_MINUTES = 129600; // 90 days

const splitPeriod = (minutes: number): { value: string; unit: PeriodUnit } => {
  if (minutes > 0 && minutes % 1440 === 0) return { value: String(minutes / 1440), unit: 'days' };
  if (minutes > 0 && minutes % 60 === 0) return { value: String(minutes / 60), unit: 'hours' };
  return { value: String(minutes), unit: minutes > 0 ? 'minutes' : 'hours' };
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const sourceLabel = (source: string): string => {
  const map: Record<string, string> = { p2p: 'P2P purchase', swap: 'Swap (inherited)' };
  return map[source] ?? source;
};

interface UserHoldingGroup {
  userId: number;
  entries: HoldingSummaryRow[];
  locksCount: number;
  nextUnlockAt: string;
}

const groupByUser = (rows: HoldingSummaryRow[]): UserHoldingGroup[] => {
  const byUser = new Map<number, HoldingSummaryRow[]>();
  for (const row of rows) {
    const list = byUser.get(row.user_id) ?? [];
    list.push(row);
    byUser.set(row.user_id, list);
  }
  const groups: UserHoldingGroup[] = [];
  byUser.forEach((entries, userId) => {
    groups.push({
      userId,
      entries,
      locksCount: entries.reduce((sum, e) => sum + e.locks_count, 0),
      nextUnlockAt: entries
        .map((e) => e.next_unlock_at)
        .sort()[0],
    });
  });
  groups.sort((a, b) => a.nextUnlockAt.localeCompare(b.nextUnlockAt));
  return groups;
};

// The holding (cooling) period is configured per section: P2P and OTC cash
// trades each have their own switch and duration; locks are listed by source.
type Props = { mode: 'p2p' | 'otc' };

const HoldingPeriodPage: React.FC<Props> = ({ mode }) => {
  const isOtc = mode === 'otc';
  const { admin } = useAuth();

  const canManage =
    admin?.role === 'super_admin' ||
    admin?.backoffice_role === 'administrator' ||
    (admin?.permissions?.includes('finance.manage_holding') ?? false);

  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState<string | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [periodValue, setPeriodValue] = useState('0');
  const [periodUnit, setPeriodUnit] = useState<PeriodUnit>('hours');
  const [saving, setSaving] = useState(false);
  const [saveBanner, setSaveBanner] = useState<string | null>(null);

  const [summary, setSummary] = useState<HoldingSummaryRow[] | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
  const [expandedLocks, setExpandedLocks] = useState<HoldingLockRecord[] | null>(null);
  const [expandedLoading, setExpandedLoading] = useState(false);
  const [releasingUserId, setReleasingUserId] = useState<number | null>(null);
  const [releaseBanner, setReleaseBanner] = useState<string | null>(null);

  const [detailUserId, setDetailUserId] = useState<number | null>(null);
  const [detailTitle, setDetailTitle] = useState('');

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const data = await getHoldingSettings();
      const scoped = (isOtc ? data.otc : data.p2p) ?? { enabled: data.enabled, period_minutes: data.period_minutes };
      setEnabled(scoped.enabled);
      const { value, unit } = splitPeriod(scoped.period_minutes);
      setPeriodValue(value);
      setPeriodUnit(unit);
    } catch (err) {
      setSettingsError(extractErrorMessage(err, 'Failed to load holding settings'));
    } finally {
      setSettingsLoading(false);
    }
  }, [isOtc]);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      setSummary(await getHoldingsSummary(mode));
    } catch (err) {
      setSummaryError(extractErrorMessage(err, 'Failed to load holdings'));
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    loadSettings();
    loadSummary();
  }, [loadSettings, loadSummary]);

  const groups = useMemo(() => groupByUser(summary ?? []), [summary]);
  const userNames = useUserNames(groups.map((g) => g.userId));

  const filteredGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return groups;
    return groups.filter((g) => {
      if (String(g.userId).includes(query)) return true;
      const info = userNames[g.userId];
      if (!info) return false;
      return [info.username, info.full_name, info.name]
        .some((v) => v && v.toLowerCase().includes(query));
    });
  }, [groups, search, userNames]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveBanner(null);

    const parsed = Number(periodValue);
    if (!Number.isInteger(parsed) || parsed < 0) {
      setSettingsError('The period must be a whole non-negative number');
      return;
    }
    const minutes = parsed * UNIT_MINUTES[periodUnit];
    if (minutes > MAX_PERIOD_MINUTES) {
      setSettingsError('The period cannot exceed 90 days');
      return;
    }
    if (enabled && minutes <= 0) {
      setSettingsError('Set a period greater than zero, or disable the holding period');
      return;
    }

    setSaving(true);
    setSettingsError(null);
    try {
      await updateHoldingSettings({ scope: mode, enabled, period_minutes: minutes });
      setSaveBanner('Settings saved');
    } catch (err) {
      setSettingsError(extractErrorMessage(err, 'Failed to save holding settings'));
    } finally {
      setSaving(false);
    }
  };

  const toggleExpand = async (userId: number) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      setExpandedLocks(null);
      return;
    }
    setExpandedUserId(userId);
    setExpandedLocks(null);
    setExpandedLoading(true);
    try {
      setExpandedLocks(await listUserHoldings(userId, undefined, mode));
    } catch (err) {
      setSummaryError(extractErrorMessage(err, 'Failed to load holding locks'));
    } finally {
      setExpandedLoading(false);
    }
  };

  const handleRelease = async (userId: number) => {
    const label = formatUserLabel(userId, userNames);
    const confirmed = window.confirm(
      `Release all frozen funds for ${label} (#${userId}) ahead of schedule?`
    );
    if (!confirmed) return;

    setReleasingUserId(userId);
    setReleaseBanner(null);
    setSummaryError(null);
    try {
      const result = await releaseUserHoldings(userId);
      setReleaseBanner(
        result.message ?? `Released ${formatCryptoAmount(result.released)} units for ${label}`
      );
      if (expandedUserId === userId) {
        setExpandedUserId(null);
        setExpandedLocks(null);
      }
      await loadSummary();
    } catch (err) {
      setSummaryError(extractErrorMessage(err, 'Failed to release holdings'));
    } finally {
      setReleasingUserId(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-white">{isOtc ? 'OTC ' : 'P2P '}Holding Period</h1>
          <p className="text-slate-400 mt-1">
            Cooling period for funds received via {isOtc ? 'OTC cash trades' : 'P2P'} — frozen funds can be swapped (the bought
            asset inherits the freeze) but cannot be withdrawn, sold on P2P, or transferred until
            the period expires
          </p>
        </div>

        <div className="card p-6 space-y-4 max-w-2xl">
          <h2 className="text-lg font-semibold text-white">Settings</h2>

          {settingsLoading ? (
            <p className="text-slate-400">Loading…</p>
          ) : (
            <form onSubmit={handleSave} className="space-y-4">
              <label className="flex items-center gap-3 text-slate-200">
                <input
                  type="checkbox"
                  checked={enabled}
                  disabled={!canManage}
                  onChange={(e) => setEnabled(e.target.checked)}
                  className="rounded border-slate-700 bg-slate-800 text-indigo-500"
                />
                Holding period enabled
              </label>

              <div className="flex items-end gap-3">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Period</label>
                  <input
                    type="number"
                    min={0}
                    value={periodValue}
                    disabled={!canManage}
                    onChange={(e) => setPeriodValue(e.target.value)}
                    className="input w-32 font-mono"
                  />
                </div>
                <select
                  value={periodUnit}
                  disabled={!canManage}
                  onChange={(e) => setPeriodUnit(e.target.value as PeriodUnit)}
                  className="input w-32"
                >
                  <option value="minutes">minutes</option>
                  <option value="hours">hours</option>
                  <option value="days">days</option>
                </select>
                {canManage && (
                  <button type="submit" disabled={saving} className="btn btn-primary py-2">
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                )}
              </div>

              <p className="text-sm text-slate-500">
                Changes apply to new P2P purchases only; existing locks keep their unlock time.
              </p>

              {settingsError && <p className="text-sm text-rose-400">{settingsError}</p>}
              {saveBanner && <p className="text-sm text-emerald-400">{saveBanner}</p>}
            </form>
          )}
        </div>

        <div className="card p-6 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-white">Users with frozen funds</h2>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input w-64"
                placeholder="Search by name, @username or ID…"
              />
              <button
                type="button"
                onClick={loadSummary}
                disabled={summaryLoading}
                className="btn btn-secondary py-2"
              >
                {summaryLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
          </div>

          {summaryError && <p className="text-sm text-rose-400">{summaryError}</p>}
          {releaseBanner && <p className="text-sm text-emerald-400">{releaseBanner}</p>}

          {summaryLoading && summary === null ? (
            <p className="text-slate-400">Loading…</p>
          ) : filteredGroups.length === 0 ? (
            <p className="text-slate-400">
              {groups.length === 0
                ? 'No users currently have frozen funds.'
                : 'No users match the search.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-header">User</th>
                    <th className="table-header text-right">Frozen</th>
                    <th className="table-header text-right">Locks</th>
                    <th className="table-header">Next unlock</th>
                    <th className="table-header text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGroups.map((group) => (
                    <React.Fragment key={group.userId}>
                      <tr>
                        <td className="table-cell">
                          <button
                            type="button"
                            onClick={() => {
                              setDetailUserId(group.userId);
                              setDetailTitle(formatUserLabel(group.userId, userNames));
                            }}
                            className="text-indigo-400 hover:text-indigo-300 font-medium"
                          >
                            {formatUserLabel(group.userId, userNames)}
                          </button>
                          <span className="text-slate-500 text-xs ml-2">#{group.userId}</span>
                        </td>
                        <td className="table-cell text-right font-mono text-slate-100">
                          {group.entries.map((entry) => (
                            <div key={entry.currency}>
                              {formatCryptoAmount(entry.frozen)}{' '}
                              <span className="text-slate-400">{entry.currency}</span>
                            </div>
                          ))}
                        </td>
                        <td className="table-cell text-right text-slate-300">{group.locksCount}</td>
                        <td className="table-cell text-slate-300">{formatDateTime(group.nextUnlockAt)}</td>
                        <td className="table-cell text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => toggleExpand(group.userId)}
                              className="btn btn-secondary py-1.5 text-sm"
                            >
                              {expandedUserId === group.userId ? 'Hide' : 'Details'}
                            </button>
                            {canManage && (
                              <button
                                type="button"
                                onClick={() => handleRelease(group.userId)}
                                disabled={releasingUserId === group.userId}
                                className="btn btn-danger py-1.5 text-sm"
                              >
                                {releasingUserId === group.userId ? 'Releasing…' : 'Release all'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {expandedUserId === group.userId && (
                        <tr>
                          <td colSpan={5} className="table-cell bg-slate-900/50">
                            {expandedLoading ? (
                              <p className="text-slate-400 py-2">Loading locks…</p>
                            ) : !expandedLocks || expandedLocks.length === 0 ? (
                              <p className="text-slate-400 py-2">No active locks.</p>
                            ) : (
                              <table className="w-full my-2">
                                <thead>
                                  <tr>
                                    <th className="table-header">Currency</th>
                                    <th className="table-header text-right">Frozen</th>
                                    <th className="table-header text-right">Originally</th>
                                    <th className="table-header">Source</th>
                                    <th className="table-header">Reference</th>
                                    <th className="table-header">Unlocks at</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {expandedLocks.map((lock) => (
                                    <tr key={lock.id}>
                                      <td className="table-cell font-medium text-slate-100">{lock.currency}</td>
                                      <td className="table-cell text-right font-mono text-slate-100">
                                        {formatCryptoAmount(lock.remaining)}
                                      </td>
                                      <td className="table-cell text-right font-mono text-slate-400">
                                        {formatCryptoAmount(lock.amount)}
                                      </td>
                                      <td className="table-cell text-slate-300">{sourceLabel(lock.source)}</td>
                                      <td className="table-cell font-mono text-xs text-slate-400">{lock.reference_id}</td>
                                      <td className="table-cell text-slate-300">{formatDateTime(lock.expires_at)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <UserDetailModal
        isOpen={detailUserId !== null}
        onClose={() => setDetailUserId(null)}
        userId={detailUserId}
        title={detailTitle}
      />
    </Layout>
  );
};

export default HoldingPeriodPage;
